import React from 'react';
import type { InvoiceItemType, DiscountType } from '@lib/api/invoices';

interface InvoiceItemInput {
  description: string;
  item_type?: InvoiceItemType;
  discount_type?: DiscountType;
  quantity: number;
  unit?: string;
  unit_price: number;
}

interface InvoiceItemsTableProps {
  items: InvoiceItemInput[];
  onItemsChange?: (items: InvoiceItemInput[]) => void;
  readOnly?: boolean;
}

export const InvoiceItemsTable: React.FC<InvoiceItemsTableProps> = ({
  items,
  onItemsChange,
  readOnly = false,
}) => {
  const handleItemChange = (index: number, field: keyof InvoiceItemInput, value: string | number) => {
    if (!onItemsChange) return;
    const newItems = [...items];
    newItems[index] = { ...newItems[index], [field]: value };
    onItemsChange(newItems);
  };

  const handleAddItem = () => {
    if (!onItemsChange) return;
    onItemsChange([...items, { description: '', item_type: 'item', quantity: 1, unit: '', unit_price: 0 }]);
  };

  const handleAddDiscount = (type: DiscountType) => {
    if (!onItemsChange) return;
    onItemsChange([
      ...items,
      {
        description: type === 'percent' ? 'Discount' : 'Discount',
        item_type: 'discount',
        discount_type: type,
        quantity: 1,
        unit: type === 'percent' ? '%' : '',
        unit_price: 0,
      },
    ]);
  };

  const handleRemoveItem = (index: number) => {
    if (!onItemsChange) return;
    onItemsChange(items.filter((_, i) => i !== index));
  };

  // Calculate subtotal of regular items only (for percentage discount calculation)
  const calculateItemsSubtotal = () => {
    return items
      .filter((item) => item.item_type !== 'discount')
      .reduce((sum, item) => sum + (Number(item.quantity) || 0) * (Number(item.unit_price) || 0), 0);
  };

  const calculateItemTotal = (item: InvoiceItemInput) => {
    if (item.item_type === 'discount') {
      if (item.discount_type === 'percent') {
        // Percentage discount: calculate based on subtotal
        const subtotal = calculateItemsSubtotal();
        return -Math.abs((subtotal * (Number(item.unit_price) || 0)) / 100);
      } else {
        // Fixed discount: use unit_price as the discount amount
        return -Math.abs((Number(item.quantity) || 1) * (Number(item.unit_price) || 0));
      }
    }
    return (Number(item.quantity) || 0) * (Number(item.unit_price) || 0);
  };

  const formatCurrency = (amount: number) => {
    return `$${(amount || 0).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const regularItems = items.filter((item) => item.item_type !== 'discount');
  const discountItems = items.filter((item) => item.item_type === 'discount');

  return (
    <div className="space-y-3 bg-white">
      {/* Table Header */}
      <div className="grid grid-cols-12 gap-2 text-sm font-medium text-gray-500 px-2 border-b border-gray-200 pb-2">
        <div className="col-span-5">Description</div>
        <div className="col-span-2 text-right">Qty</div>
        <div className="col-span-1 text-center">Unit</div>
        <div className="col-span-2 text-right">Unit Price</div>
        <div className="col-span-2 text-right">Total</div>
      </div>

      {/* Regular Items */}
      {regularItems.map((item) => {
        const index = items.findIndex((i) => i === item);
        return (
          <div
            key={index}
            className="grid grid-cols-12 gap-2 items-center bg-gray-50 p-2 rounded-lg border border-gray-200"
          >
            {readOnly ? (
              <>
                <div className="col-span-5 text-gray-900">{item.description}</div>
                <div className="col-span-2 text-right text-gray-900">{item.quantity}</div>
                <div className="col-span-1 text-center text-gray-500">{item.unit || '-'}</div>
                <div className="col-span-2 text-right text-gray-900">{formatCurrency(item.unit_price)}</div>
                <div className="col-span-2 text-right font-medium text-gray-900">
                  {formatCurrency(calculateItemTotal(item))}
                </div>
              </>
            ) : (
              <>
                <div className="col-span-5">
                  <input
                    type="text"
                    value={item.description}
                    onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                    placeholder="Item description"
                    className="w-full px-3 py-1.5 rounded bg-white text-gray-900 border border-gray-300 focus:border-primary focus:ring-1 focus:ring-primary focus:outline-none text-sm"
                  />
                </div>
                <div className="col-span-2">
                  <input
                    type="number"
                    value={item.quantity}
                    onChange={(e) => handleItemChange(index, 'quantity', parseFloat(e.target.value) || 0)}
                    min="0"
                    step="0.01"
                    className="w-full px-3 py-1.5 rounded bg-white text-gray-900 border border-gray-300 focus:border-primary focus:ring-1 focus:ring-primary focus:outline-none text-sm text-right"
                  />
                </div>
                <div className="col-span-1">
                  <input
                    type="text"
                    value={item.unit || ''}
                    onChange={(e) => handleItemChange(index, 'unit', e.target.value)}
                    placeholder="ea"
                    className="w-full px-2 py-1.5 rounded bg-white text-gray-900 border border-gray-300 focus:border-primary focus:ring-1 focus:ring-primary focus:outline-none text-sm text-center"
                  />
                </div>
                <div className="col-span-2">
                  <input
                    type="number"
                    value={item.unit_price}
                    onChange={(e) => handleItemChange(index, 'unit_price', parseFloat(e.target.value) || 0)}
                    min="0"
                    step="0.01"
                    className="w-full px-3 py-1.5 rounded bg-white text-gray-900 border border-gray-300 focus:border-primary focus:ring-1 focus:ring-primary focus:outline-none text-sm text-right"
                  />
                </div>
                <div className="col-span-1 text-right font-medium text-gray-900">
                  {formatCurrency(calculateItemTotal(item))}
                </div>
                <div className="col-span-1 flex justify-end">
                  <button
                    type="button"
                    onClick={() => handleRemoveItem(index)}
                    className="text-red-500 hover:text-red-700 transition-colors p-1"
                    title="Remove item"
                  >
                    <span className="material-symbols-outlined text-lg">delete</span>
                  </button>
                </div>
              </>
            )}
          </div>
        );
      })}

      {/* Discount Items */}
      {discountItems.length > 0 && (
        <div className="border-t border-gray-200 pt-3 mt-3">
          <div className="text-sm font-medium text-green-600 mb-2 flex items-center gap-2">
            <span className="material-symbols-outlined text-lg">sell</span>
            Discounts
          </div>
          {discountItems.map((item) => {
            const index = items.findIndex((i) => i === item);
            const isPercent = item.discount_type === 'percent';
            return (
              <div
                key={index}
                className="grid grid-cols-12 gap-2 items-center bg-green-50 p-2 rounded-lg border border-green-200 mb-2"
              >
                {readOnly ? (
                  <>
                    <div className="col-span-5 text-green-700 flex items-center gap-2">
                      <span className="material-symbols-outlined text-sm">discount</span>
                      {item.description}
                      {isPercent && <span className="text-xs text-green-600">({item.unit_price}%)</span>}
                    </div>
                    <div className="col-span-2 text-right text-green-700">
                      {isPercent ? '-' : item.quantity}
                    </div>
                    <div className="col-span-1 text-center text-green-600">
                      {isPercent ? '%' : item.unit || '-'}
                    </div>
                    <div className="col-span-2 text-right text-green-700">
                      {isPercent ? `${item.unit_price}%` : formatCurrency(item.unit_price)}
                    </div>
                    <div className="col-span-2 text-right font-medium text-green-700">
                      {formatCurrency(calculateItemTotal(item))}
                    </div>
                  </>
                ) : (
                  <>
                    <div className="col-span-5">
                      <div className="flex items-center gap-2">
                        <span className="material-symbols-outlined text-green-600 text-lg">discount</span>
                        <input
                          type="text"
                          value={item.description}
                          onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                          placeholder="Discount description"
                          className="flex-1 px-3 py-1.5 rounded bg-white text-gray-900 border border-green-300 focus:border-green-500 focus:ring-1 focus:ring-green-500 focus:outline-none text-sm"
                        />
                      </div>
                    </div>
                    <div className="col-span-2">
                      {isPercent ? (
                        <div className="text-center text-green-600 text-sm py-1.5">-</div>
                      ) : (
                        <input
                          type="number"
                          value={item.quantity}
                          onChange={(e) => handleItemChange(index, 'quantity', parseFloat(e.target.value) || 1)}
                          min="1"
                          step="1"
                          className="w-full px-3 py-1.5 rounded bg-white text-gray-900 border border-green-300 focus:border-green-500 focus:ring-1 focus:ring-green-500 focus:outline-none text-sm text-right"
                        />
                      )}
                    </div>
                    <div className="col-span-1 text-center text-green-600 text-sm py-1.5">
                      {isPercent ? '%' : '$'}
                    </div>
                    <div className="col-span-2">
                      <input
                        type="number"
                        value={item.unit_price}
                        onChange={(e) => handleItemChange(index, 'unit_price', parseFloat(e.target.value) || 0)}
                        min="0"
                        step={isPercent ? '0.5' : '0.01'}
                        max={isPercent ? '100' : undefined}
                        placeholder={isPercent ? 'Percent' : 'Amount'}
                        className="w-full px-3 py-1.5 rounded bg-white text-gray-900 border border-green-300 focus:border-green-500 focus:ring-1 focus:ring-green-500 focus:outline-none text-sm text-right"
                      />
                    </div>
                    <div className="col-span-1 text-right font-medium text-green-700">
                      {formatCurrency(calculateItemTotal(item))}
                    </div>
                    <div className="col-span-1 flex justify-end">
                      <button
                        type="button"
                        onClick={() => handleRemoveItem(index)}
                        className="text-red-500 hover:text-red-700 transition-colors p-1"
                        title="Remove discount"
                      >
                        <span className="material-symbols-outlined text-lg">delete</span>
                      </button>
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Action Buttons */}
      {!readOnly && (
        <div className="flex items-center gap-4 mt-2">
          <button
            type="button"
            onClick={handleAddItem}
            className="flex items-center gap-2 text-primary hover:text-primary/80 transition-colors text-sm font-medium"
          >
            <span className="material-symbols-outlined text-lg">add</span>
            Add Line Item
          </button>
          <div className="h-4 w-px bg-gray-300" />
          <button
            type="button"
            onClick={() => handleAddDiscount('fixed')}
            className="flex items-center gap-2 text-green-600 hover:text-green-700 transition-colors text-sm font-medium"
          >
            <span className="material-symbols-outlined text-lg">remove_circle</span>
            Fixed Discount
          </button>
          <button
            type="button"
            onClick={() => handleAddDiscount('percent')}
            className="flex items-center gap-2 text-green-600 hover:text-green-700 transition-colors text-sm font-medium"
          >
            <span className="material-symbols-outlined text-lg">percent</span>
            % Discount
          </button>
        </div>
      )}

      {/* Empty State */}
      {items.length === 0 && (
        <div className="text-center py-8 text-gray-500">
          No items added yet
        </div>
      )}
    </div>
  );
};

// Export helper for calculating totals (used by InvoiceModal)
export const calculateInvoiceItemTotal = (
  item: InvoiceItemInput,
  allItems: InvoiceItemInput[]
): number => {
  if (item.item_type === 'discount') {
    if (item.discount_type === 'percent') {
      const subtotal = allItems
        .filter((i) => i.item_type !== 'discount')
        .reduce((sum, i) => sum + (Number(i.quantity) || 0) * (Number(i.unit_price) || 0), 0);
      return -Math.abs((subtotal * (Number(item.unit_price) || 0)) / 100);
    } else {
      return -Math.abs((Number(item.quantity) || 1) * (Number(item.unit_price) || 0));
    }
  }
  return (Number(item.quantity) || 0) * (Number(item.unit_price) || 0);
};
